import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormControl,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { nameof } from '@bds/core';
import {
    BdsErpcOriginService,
    BdsSplcErpcService,
    BdsSupplierService,
    RtCarAssignService,
    RtCarMechService,
    RtRouteService,
} from '@bds/data-access';
import { BdsCustomerService } from '@bds/data-access';
import { EquipmentService } from '@bds/equipment';
import { FormErrorHandlerService } from '@bds/helpers';
import {
    RtBusinessGroup,
    RtCommodityShipped,
    RtErpcOrigin,
    RtFleetName,
    RtRouteCode,
    RtRouteCodeAdapter,
    RtSupplierFact,
    RtTrip,
    RtvTripError,
    RtvTripErrorMetadata,
} from '@bds/railtrac-models';
import { RTSupplierFact, RtCustomerFact } from '@bds/reference-models';
import { BdsSupplierSearchDialogModel, dxMatStyle } from '@bds/smart-components';
import {
    faArrowDown,
    faBell,
    faBellExclamation,
    faBellOn,
    faBellSlash,
    faClock,
    faCogs,
    faDollarSign,
    faExclamationTriangle,
    faFireAlt,
    faFlag,
    faQuestion,
} from '@fortawesome/pro-duotone-svg-icons';
import {
    faCircle,
    faCommentAltPlus,
    faDotCircle,
    faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import { faEdit, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import DataSource from 'devextreme/data/data_source';
import * as moment from 'moment';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { skip, take } from 'rxjs/operators';
import { BdsBusinessGroupService } from '../../bds-business-group/bds-business-group.service';
import { BdsFleetService } from '../../bds-fleet/bds-fleet.service';
import { BdsSightCodeService } from '../../bds-sight-code/bds-sight-code.service';
import { BdsRouteSearchDialogModel, BdsRouteSearchDialogComponent } from '@bds/route';
import { RtShipmentTypeService } from '../../rt-shipment-type/rt-shipment-type.service';
import { BdsStateService } from '@bds/data-access';
import {
    BdsCustomerSearchDialogComponent,
    BdsCustomerSearchDialogModel,
} from '@bds/smart-components';
import { RtCustomerTypeDefaultService } from '@bds/data-access';
import { ReferenceApplicationPathProvider } from '@bds/reference-pathing';
import ODataStore from 'devextreme/data/odata/store';
import { BdsSupplierSearchDialogComponent } from '@bds/smart-components';
import { RtTripErrorService } from '@bds/railtrac';

@Component({
    selector: 'rt-trip-error-details',
    templateUrl: './rt-trip-error-details.component.html',
    styleUrls: ['./rt-trip-error-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class RtTripErrorDetailsComponent implements OnInit, OnChanges, OnDestroy {
    get ormId(): number {
        return this.tripError?.ormId;
    }

    get isNew(): boolean {
        return !this.ormId;
    }

    get lastCommentCode(): string {
        return 'X';
    }

    get isHotlistedComment(): boolean {
        switch (this.lastCommentCode) {
            case 'SD':
            case 'H':
            case 'M':
            case 'L':
                return true;
            default:
                return false;
        }
    }

    get recordProcDate(): string {
        return this.tripError?.procDateTime ? moment(this.tripError?.procDateTime).calendar() : '';
    }

    get interfaceValidationErrorCount(): number {
        return 0;
    }

    get isFreeRunner(): boolean {
        return this.tripForm && this.getControl('freeRunner').value !== 'N';
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    get dxMatStyle(): string {
        return dxMatStyle(this.matStyle);
    }

    @ViewChild('consigneeSelectBox') consigneeSelectBox: DxSelectBoxComponent;
    @ViewChild('routeCodeSelectBox') routeCodeSelectBox: DxSelectBoxComponent;
    @Input() tripError: RtvTripError;
    @Input() tripErrorOrmIds: number[];
    @Output() tripValuesChange = new EventEmitter<RtTrip>();
    @Output() formDirtyChange = new EventEmitter<boolean>();

    @Input() allowChangeKey = false;
    @Input() allowPopout = false;
    @Input() allowAddComment = true;
    @Input() highlightDirty = false;
    @Input() multipleTrips = false;
    @Input() showWarnings = true;
    @Input() showMenu = true;
    @Input() readonly = false;
    @Input() matStyle: 'fill' | 'outline' | 'standard' | 'legacy' = 'fill';
    @Input() dense = true;

    @Input() dirty: boolean;
    @Output() dirtyChange = new EventEmitter<boolean>();
    @Output() tripUpdate = new EventEmitter<{ [key: string]: any }>();
    @Output() formValidChange = new EventEmitter<boolean>(true);

    // TODO: This doesn't appear to be used anywhere. If it is assigned in a parent in the future
    // or if it is in use, need to add dirty values of Trip Ref Data
    @Output() dirtyValues = new EventEmitter<Map<string, unknown> | unknown[] | undefined>();
    @Output() addComment = new EventEmitter<void>();
    @Output() equipmentChange: EventEmitter<number> = new EventEmitter();
    @Output() navigateClmSection = new EventEmitter();
    @Output() notifyToUpdateTrip: EventEmitter<any> = new EventEmitter<any>();

    private initialStatusCheckTrigger = new Subject<void>();

    canEditReturnCityState = false;
    busGrpSetFromEquipment = false;
    disabledFields = true;
    fleetSetFromEquipment = false;
    isLoadingCommodities = false;
    groupIndent = 'pl-0';
    headerSize = 'h3';
    matInputStyle = 'fill';
    matResultStyle = 'standard';
    matFloatLabel = 'always';

    iconEdit = faEdit;
    iconPopout = faExternalLink;
    iconAddComment = faCommentAltPlus;

    dirtyTripFormData: any;
    tripForm: UntypedFormGroup;
    tripFormValueChangeSubscription: Subscription;
    jeopardyStatus: string[];
    isInitializing = false;

    highlightDirtyOriginCity = false;

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //// Angular Form Support in DevExtreme
    //// https://js.devexpress.com/Documentation/Guide/Angular_Components/Component_Configuration_Syntax/#Angular_Forms_Support
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    customerFactDxLookupSource: DataSource;
    customerFactDxLookupSearchExpr: string[];
    customerFactDxLookupValueExpr: string;
    customerNoFieldServerName: string;
    customerNameFieldServerName: string;
    careOfNameFieldServerName: string;
    selectedCustomer: RtCustomerFact;
    selectedSupplier: RtSupplierFact;
    commoditiesShipped: RtCommodityShipped[] = [];

    supplierFactDxLookupSource: DataSource;
    supplierCodeFieldServerName: string;
    supplierNameFieldServerName: string;
    supplierFactDxLookupValueExpr: string;
    supplierFactDxLookupSearchExpr: string[];

    routeCodeDxLookupSource: DataSource;
    erpcOriginDxLookupSource: ODataStore;
    routeCodeDxLookupSearchExpr: string[];
    routeCodeDxLookupValueExpr: string;
    routeCodeFieldServerName: string;
    erpcOriginServerName: string;
    routeDescriptionFieldServerName: string;
    selectedRoute: RtRouteCode;
    customerName = '';

    suppliers: RtSupplierFact[] = [];

    businessGroupDxLookupSource: DataSource;
    fleetDxLookupSource: DataSource;

    //// Trip Ref Information ////
    dirtyRefData: any;
    tripRefData: any;
    tripRefsValid: boolean;
    //////////////////

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Shipment Alert Section //////////////////////////////////////////////////////////////////////////////////////////////////
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    hoverClass = 'mat-elevation-z8';
    iconJeopardy = faFlag;
    iconHotListGeneral = faFireAlt;
    iconHotListMechanical = faCogs;
    iconHotListLease = faDollarSign;
    iconHotListTrouble = faClock;
    iconCommentSD = faBellOn;
    iconCommentH = faBellExclamation;
    iconCommentM = faBell;
    iconCommentL1 = faBell;
    iconCommentL2 = faArrowDown;
    iconComment0 = faBellSlash;
    iconInterfaceValiationErrors = faExclamationTriangle;
    iconCircle = faCircle;
    iconDotCircle = faDotCircle;
    iconSearch = faSearch;
    iconUnknownEquipment = faQuestion;
    iconWarning = faExclamationTriangle;

    hideJeopardized = false;
    hideHotlistGeneral = false;
    hideHotlistMechanical = false;
    hideHotlistLease = false;
    hideHotlistTrouble = false;
    hideHotlistCommentCode = false;
    hideHotlistInterfaceErrors = false;
    showNewEquipmentMsg = false;
    showTripChangedFleet = false;
    showTripChangedBusGrp = false;
    isSetRoute = true;

    carStatusManuallyUpdated = false;
    shipmentTypeManuallyUpdated = false;

    public createAsLoadAndHold: boolean;

    @ViewChild('routeCodeLookup') routeCodeLookupComponent: DxSelectBoxComponent;

    routeCodeSearchInlineButtonOptions = {
        type: 'default',
        icon: 'search',
        stylingMode: 'text',
        onClick: (): void => {
            this.searchForRouteCode();
        },
    };

    consigneeSearchInlineButtonOptions = {
        type: 'default',
        icon: 'search',
        stylingMode: 'text',
        onClick: (): void => {
            this.searchForConsignee();
        },
    };

    consigneeAddInlineButtonOptions = {
        type: 'default',
        icon: 'add',
        stylingMode: 'text',
        onClick: (): void => {
            this.openConsigneeManager();
        },
    };

    consigneeEditInlineButtonOptions = {
        type: 'default',
        icon: 'edit',
        stylingMode: 'text',
        onClick: (): void => {
            this.openConsigneeManager(this.selectedCustomer);
        },
    };

    routeAddInlineButtonOptions = {
        type: 'default',
        icon: 'add',
        stylingMode: 'text',
        onClick: (): void => {
            this.openRouteManager();
        },
    };

    routeEditInlineButtonOptions = {
        type: 'default',
        icon: 'edit',
        stylingMode: 'text',
        onClick: (): void => {
            this.openRouteManager(this.selectedRoute);
        },
    };

    supplierSearchInlineButtonOptions = {
        type: 'default',
        icon: 'search',
        stylingMode: 'text',
        onClick: (): void => {
            this.searchForSupplier();
        },
    };

    constructor(
        public tripErrorMetadata: RtvTripErrorMetadata,
        public stateApiService: BdsStateService,
        public supplierApiService: BdsSupplierService,
        public sightCodeService: BdsSightCodeService,
        public shipmentTypeService: RtShipmentTypeService,
        public customerApiService: BdsCustomerService,
        public customerTypeDefaultService: RtCustomerTypeDefaultService,
        public routeApiService: RtRouteService,
        public routeApiAdapter: RtRouteCodeAdapter,
        public businessGroupApiService: BdsBusinessGroupService,
        public fleetApiService: BdsFleetService,
        public tripErrorService: RtTripErrorService,
        public dialog: MatDialog,
        public snackbar: MatSnackBar,
        public equipmentService: EquipmentService,
        public carAssignService: RtCarAssignService,
        public carMechService: RtCarMechService,
        private formErrorService: FormErrorHandlerService,
        private originCodeService: BdsErpcOriginService,
        public referenceAppPathProvider: ReferenceApplicationPathProvider,
        public splcService: BdsSplcErpcService,
        public rtCustomerService: BdsCustomerService,
    ) {
        this.tripForm = tripErrorMetadata.getFormGroup();

        this.tripFormValueChangeSubscription = new Subscription();

        this.routeCodeFieldServerName = this.routeApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtRouteCode>('routeCode'),
        ).server;
        this.routeDescriptionFieldServerName = this.routeApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtRouteCode>('routeDscr'),
        ).server;

        this.erpcOriginServerName = this.originCodeService.adapter.metadata.find(
            (f) => f.client === nameof<RtErpcOrigin>('erpcOrigin'),
        ).server;

        this.customerNoFieldServerName = this.customerApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtCustomerFact>('customerNo'),
        ).server;
        this.customerNameFieldServerName = this.customerApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtCustomerFact>('customerName'),
        ).server;
        this.careOfNameFieldServerName = this.customerApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtCustomerFact>('careOfName'),
        ).server;
        this.supplierCodeFieldServerName = this.supplierApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtSupplierFact>('code'),
        ).server;
        this.supplierNameFieldServerName = this.supplierApiService.adapter.metadata.find(
            (f) => f.client === nameof<RtSupplierFact>('name'),
        ).server;
    }

    ngOnInit() {
        this.formValidChange?.emit(!!this.ormId);

        this.tripFormValueChangeSubscription = this.tripForm.valueChanges.subscribe(() => {
            if (!this.isInitializing && this.tripForm.dirty) {
                this.formDirtyChange.emit(true);
            }
        });

        this.customerFactDxLookupSource = new DataSource({
            store: this.customerApiService.getODataStore(),
            searchExpr: [
                this.customerNoFieldServerName,
                this.customerNameFieldServerName,
                this.careOfNameFieldServerName,
            ],
            paginate: true,
            pageSize: 100,
        });

        this.supplierFactDxLookupSource = new DataSource({
            store: this.supplierApiService.getODataStore(),
            searchExpr: [this.supplierCodeFieldServerName, this.supplierNameFieldServerName],
            paginate: true,
            pageSize: 100,
        });

        this.customerFactDxLookupSearchExpr = [
            this.customerNoFieldServerName,
            this.customerNameFieldServerName,
            this.careOfNameFieldServerName,
        ];

        this.supplierFactDxLookupSearchExpr = [
            this.supplierCodeFieldServerName,
            this.supplierNameFieldServerName,
        ];

        const supplierStore = this.supplierApiService.getODataStore();
        if (supplierStore) {
            void supplierStore.load().then((x) => {
                this.suppliers = x;
            });
        }

        this.customerFactDxLookupValueExpr = this.customerNoFieldServerName;
        this.supplierFactDxLookupValueExpr = this.supplierCodeFieldServerName;

        this.businessGroupDxLookupSource = new DataSource({
            store: this.businessGroupApiService.getODataStore(),
            searchExpr: [
                this.businessGroupApiService.adapter.metadata.find(
                    (f) => f.client === nameof<RtBusinessGroup>('businessGroup'),
                ).server,
                this.businessGroupApiService.adapter.metadata.find(
                    (f) => f.client === nameof<RtBusinessGroup>('businessGroupName'),
                ).server,
            ],
            paginate: true,
            pageSize: 100,
        });

        this.fleetDxLookupSource = new DataSource({
            store: this.fleetApiService.getODataStore(),
            searchExpr: [
                this.fleetApiService.adapter.metadata.find(
                    (f) => f.client === nameof<RtFleetName>('fleetId'),
                ).server,
                this.fleetApiService.adapter.metadata.find(
                    (f) => f.client === nameof<RtFleetName>('fleetName'),
                ).server,
            ],
            paginate: true,
            pageSize: 100,
        });

        this.routeCodeDxLookupSource = new DataSource({
            store: this.routeApiService.getODataStore(),
            searchExpr: [this.routeCodeFieldServerName, this.routeDescriptionFieldServerName],
            paginate: true,
            pageSize: 100,
            filter: ['isActive', '=', 'Y'],
        });

        this.erpcOriginDxLookupSource = this.splcService.getODataStore();

        this.routeCodeDxLookupSearchExpr = [
            this.routeCodeFieldServerName,
            this.routeDescriptionFieldServerName,
        ];

        this.routeCodeDxLookupValueExpr = this.routeCodeFieldServerName;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tripError']) {
            this.isInitializing = true;

            this.tripForm.reset(this.tripError);
            const shipmentTypeControl = this.getControl('shipmentType');
            shipmentTypeControl.setValue(this.tripError?.shipmentType);

            void this.getCommodityShipped();
            this.loadCustomerName();

            this.getControl('fleetId').setValue(
                this.tripError?.fleetId?.trim() || (this.multipleTrips ? '' : 'UNKN'),
            );
            this.getControl('businessGroup').setValue(
                this.tripError?.businessGroup?.trim() || (this.multipleTrips ? '' : 'UNKN'),
            );

            Promise.resolve().then(() => {
                this.tripForm.markAsPristine();
                this.isInitializing = false;
            });
        }
    }

    ngOnDestroy() {
        this.tripFormValueChangeSubscription.unsubscribe();
    }

    private loadCustomerName(): void {
        if (this.tripError?.custNo) {
            this.rtCustomerService
                .getByCustNo(this.tripError?.custNo)
                .pipe(take(1))
                .subscribe({
                    next: (customer) => {
                        this.customerName = customer?.customerName || '';
                    },
                    error: (error) => {
                        console.error('Error loading customer name:', error);
                        this.customerName = '';
                    },
                });
        } else {
            this.customerName = '';
        }
    }

    private setValidators() {
        this.tripErrorMetadata.setValidators(
            this.tripForm,
            this.createAsLoadAndHold,
            this.tripError?.ormId,
        );
        this.tripForm.updateValueAndValidity();
    }

    routeCodeDxLookupDisplayExpr = (route: any) => {
        if (!route) {
            return '';
        }

        if (this.isSetRoute) {
            this.setValidators();
        }
        this.isSetRoute = false;

        const routeCode = route[this.routeCodeFieldServerName];
        const routeDscr = route[this.routeDescriptionFieldServerName];
        return `${routeCode} - ${routeDscr}`;
    };

    customerFactDxLookupDisplayExpr = (cust: any) => {
        if (!cust) {
            return '';
        }
        const custNo = cust[this.customerNoFieldServerName];
        const custName = cust[this.customerNameFieldServerName];
        const careOfName = cust[this.careOfNameFieldServerName];
        return `${custNo} - ${custName}`;
    };

    supplierFactDxLookupDisplayExpr = (sup: any) => {
        if (!sup) {
            return '';
        }
        const supCode = sup[this.supplierCodeFieldServerName];
        const supName = sup[this.supplierNameFieldServerName];
        return `${supName} - ${supCode}`;
    };

    supplierFactInputDxLookupDisplayExpr = (sup: any) => {
        if (!sup) {
            return '';
        }
        const supCode = sup[this.supplierCodeFieldServerName];
        const supName = sup[this.supplierNameFieldServerName];
        return `${supName} - ${supCode}`;
    };

    /**
     * Get error text based on the validation of the specified form item.
     * @param formItem The item on a form that has validation.
     */
    getErrorText(formItem: UntypedFormControl | UntypedFormGroup | UntypedFormArray): string {
        return this.formErrorService.getFormError(formItem);
    }

    kvpDisplayTemplate(key: string, value: string): string {
        const split = !!key && !!value ? ' - ' : '';
        return `${key}${split}${value}`;
    }

    onRouteCodeChange(e): void {
        this.selectedRoute = this.routeApiAdapter.adapt(e.selectedItem);
    }

    /**
     * Handler for onCustomItemCreating on a dxSelectBox where the value is just text.
     * @param e The Event.
     */
    onDxSelectBoxCustomItemUpper(e): void {
        e.customItem = `${e.text || ''}`.toUpperCase();
    }

    async fetchCarAssignInfo(): Promise<void> {
        const init = (this.getControl('carInit').value as string) || ('' as string);
        const no = (this.getControl('carNo').value as string) || ('' as string);

        this.busGrpSetFromEquipment = true;
        this.fleetSetFromEquipment = true;

        this.getControl('freeRunner').setValue('Y');
        this.getControl('oneWayRound').setValue('O');
        try {
            const carAssignResult = await firstValueFrom(this.carAssignService.read(init, no));

            if (carAssignResult) {
                this.getControl('businessGroup').setValue(carAssignResult?.businessGroup);
                this.getControl('fleetId').setValue(carAssignResult.fleetId);

                this.getControl('freeRunner').setValue('N');
                this.getControl('oneWayRound').setValue('R');
                return;
            }
        } catch (error) {
            const carMechResult = await firstValueFrom(this.carMechService.read(init, no));

            if (carMechResult) {
                this.getControl('businessGroup').setValue(carMechResult?.businessGroup);
                this.getControl('fleetId').setValue(carMechResult?.fleetId);

                this.getControl('freeRunner').setValue('N');
                this.getControl('oneWayRound').setValue('R');
                return;
            }
        }

        this.getControl('businessGroup').setValue(null);
        this.getControl('fleetId').setValue(null);
        this.getControl('freeRunner').setValue('Y');
        this.getControl('oneWayRound').setValue('O');

        this.showTripChangedBusGrp = false;
        this.showTripChangedFleet = false;
    }

    onCustomerFactChanged(e: any): void {
        if (this.tripError?.ormId) {
            //Only applies to edit trip
        }
        //if trip initiated Status = 1, then allow user to edit Return City/State
        this.initialStatusCheckTrigger.next();
    }

    async onSupplierFactChanged(e: any): Promise<void> {
        const a = await firstValueFrom(
            this.supplierApiService.getSupplierByCode(e?.value as string),
        );

        if (a?.length) {
            this.getControl('supplierName').setValue(a[0]?.name ?? '');
            return;
        }
        this.getControl('supplierName').reset();
    }

    getCommodityShipped(): void {
        this.isLoadingCommodities = true;
        this.commoditiesShipped = [];
        this.tripErrorService
            .getCommoditiesShipped(
                this.multipleTrips ? this.tripErrorOrmIds : [this.tripError?.ormId],
            )
            .subscribe({
                next: (cs) => {
                    this.commoditiesShipped = cs;
                },
                error: (err) => {
                    console.error('Failed to fetch commodities:', err);
                },
                complete: () => {
                    this.isLoadingCommodities = false;
                },
            });
    }

    searchForRouteCode(): void {
        this.routeCodeSelectBox?.instance?.close();
        const data = new BdsRouteSearchDialogModel();
        data.actionText = 'Select Route';
        data.title = 'Select Route';

        const dialogRef = this.dialog.open(BdsRouteSearchDialogComponent, {
            data: data,
        });
        dialogRef.afterClosed().subscribe((s: RtRouteCode) => {
            if (s) {
                this.getControl('routeCode').setValue(s.routeCode);
                this.getControl('routeCode').markAsDirty();
                this.selectedRoute = s;
                this.formDirtyChange.emit(true);
            }
        });
    }

    searchForConsignee(): void {
        if (this.consigneeSelectBox) {
            this.consigneeSelectBox.instance.close();
        }

        const data = new BdsCustomerSearchDialogModel();
        data.actionText = 'Select Consignee';
        data.title = 'Select Consignee';
        data.source = this.customerFactDxLookupSource;
        data.defaultFastracName = 'SEARCH CONSIGNEE';

        const dialogRef = this.dialog.open(BdsCustomerSearchDialogComponent, {
            data: data,
        });
        dialogRef.afterClosed().subscribe((s: RtCustomerFact) => {
            if (!!s) {
                this.getControl('custNo').setValue(s.customerNo);
                this.getControl('custNo').markAsDirty();
                this.selectedCustomer = s;
                this.formDirtyChange.emit(true);
            }
        });
    }

    searchForSupplier(): void {
        const data = new BdsSupplierSearchDialogModel();
        data.actionText = 'Select Supplier';
        data.title = 'Select Supplier';
        data.source = this.supplierFactDxLookupSource;
        data.defaultFastracName = 'SEARCH SUPPLIER';
        const dialogRef = this.dialog.open(BdsSupplierSearchDialogComponent, {
            data: data,
        });
        dialogRef.afterClosed().subscribe((s: RTSupplierFact) => {
            if (s) {
                this.getControl('supplierCode').setValue(s.code);
                this.getControl('supplierCode').markAsDirty();
                this.selectedSupplier = s;
                this.formDirtyChange.emit(true);
            }
        });
    }

    addCommentClicked(): void {
        this.addComment.emit();
    }

    manualUpdate(): void {
        this.carStatusManuallyUpdated = true;
        this.shipmentTypeManuallyUpdated = true;
    }

    onUserInput(): void {
        this.formDirtyChange.emit(true);
    }

    openConsigneeManager(item?: RtCustomerFact): void {
        if (item) {
            this.referenceAppPathProvider
                .Customer(item.customerNo)
                .pipe(take(1))
                .subscribe((refUrl) => {
                    window.open(refUrl, '_blank');
                });
        } else {
            this.referenceAppPathProvider
                .Customers()
                .pipe(take(1))
                .subscribe((refUrl) => {
                    window.open(refUrl, '_blank');
                });
        }
    }

    openRouteManager(item?: RtRouteCode): void {
        if (item) {
            window.open('routes/detail/' + item.routeCode, '_blank').focus();
        } else {
            window.open('routes/create', '_blank').focus();
        }
    }

    onReturnCitySelected(splc: RtErpcOrigin): void {
        if (splc) {
            setTimeout(() => {
                this.getControl('returnCity').setValue(splc.erpcOrigin);
                this.getControl('returnState').setValue(splc.erpcState);
                this.getControl('returnCity').markAsDirty();
                this.getControl('returnState').markAsDirty();
            }, 0);
        }
    }

    private getControl(key: keyof RtvTripError): FormControl {
        return this.tripForm.get(key) as FormControl;
    }
}
